<template>
  <div class="permission-editor">
    <div class="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
      <div>
        <h5 class="mb-1">Permissions</h5>
        <p class="mb-0 text-muted small">{{ description }}</p>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-2">
        <span class="badge rounded-pill summary-badge">{{ grantedCount }} of {{ allPermissions.length }} granted</span>
        <template v-if="isUserMode">
          <span v-if="addedCount" class="badge rounded-pill bg-success-subtle text-success">+{{ addedCount }} added</span>
          <span v-if="removedCount" class="badge rounded-pill bg-danger-subtle text-danger">&minus;{{ removedCount }} removed</span>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="!addedCount && !removedCount"
                  @click="resetOverrides">
            Reset to role defaults
          </button>
        </template>
      </div>
    </div>

    <div class="permission-panes">
      <nav class="permission-nav">
        <button type="button" class="nav-item-button" :class="{ active: selectedGroup === null }"
                @click="selectedGroup = null">
          <span class="flex-grow-1">All permissions</span>
          <span v-if="overrideDotClass(allPermissions)" :class="['override-dot', overrideDotClass(allPermissions)]"></span>
          <span class="group-count">{{ countGranted(allPermissions) }}/{{ allPermissions.length }}</span>
        </button>
        <hr class="my-2"/>
        <button v-for="group in groups" :key="group.name" type="button" class="nav-item-button"
                :class="{ active: selectedGroup === group.name }" @click="selectedGroup = group.name">
          <span class="flex-grow-1">{{ group.name }}</span>
          <span v-if="overrideDotClass(group.permissions)" :class="['override-dot', overrideDotClass(group.permissions)]"></span>
          <span class="group-count">{{ countGranted(group.permissions) }}/{{ group.permissions.length }}</span>
        </button>
      </nav>

      <div class="permission-pane">
        <div class="permission-toolbar">
          <h6 class="mb-0 flex-grow-1">{{ selectedGroup || 'All permissions' }}</h6>
          <div class="search-box">
            <i class="fa fa-search text-muted"></i>
            <input type="search" class="form-control form-control-sm border-0 shadow-none" placeholder="Search permissions"
                   v-model="search" @keydown.enter.prevent/>
          </div>
          <div class="filter-toggle" role="group">
            <button v-for="option in filterOptions" :key="option.value" type="button"
                    :class="{ active: filter === option.value }" @click="filter = option.value">
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="permission-list">
          <template v-for="group in visibleGroups" :key="group.name">
            <div v-if="selectedGroup === null" class="group-heading">{{ group.name }}</div>
            <div v-for="permission in group.permissions" :key="permission"
                 :class="['permission-row', sourceOf(permission), { 'not-granted': !isGranted(permission) }]">
              <label class="permission-label" :for="permissionId(permission)">
                <span class="permission-name">{{ permissionLabel(permission) }}</span>
                <small v-if="isUserMode" class="permission-source">{{ sourceLabel(permission) }}</small>
              </label>
              <button v-if="isUserMode && isOverridden(permission)" type="button" class="btn btn-link btn-sm undo-button"
                      title="Revert to role default" @click="toggle(permission)">
                <i class="fa fa-undo"></i>
              </button>
              <div class="form-check form-switch mb-0">
                <input :id="permissionId(permission)" class="form-check-input" type="checkbox" role="switch"
                       :checked="isGranted(permission)" @change="toggle(permission)"/>
              </div>
            </div>
          </template>
          <div v-if="!visibleGroups.length" class="text-center text-muted small py-5">No permissions match.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { PERMISSION_GROUPS, permissionLabel } from '@/constants/permissions';

const props = defineProps({
  mode: { type: String, default: 'role', validator: value => ['role', 'user'].includes(value) },
  permissions: { type: Array, default: () => [] },
  rolePermissions: { type: Array, default: () => [] },
  addPermissions: { type: Array, default: () => [] },
  removePermissions: { type: Array, default: () => [] },
  roleName: { type: String, default: '' },
  userName: { type: String, default: '' },
});

const emit = defineEmits(['update:permissions', 'update:addPermissions', 'update:removePermissions']);

const groups = Object.entries(PERMISSION_GROUPS)
  .map(([name, permissions]) => ({ name, permissions }))
  .sort((a, b) => a.name.localeCompare(b.name));
const allPermissions = groups.flatMap(group => group.permissions);

const selectedGroup = ref(null);
const search = ref('');
const filter = ref('all');

const isUserMode = computed(() => props.mode === 'user');
const userLabel = computed(() => props.userName || 'this user');

const filterOptions = computed(() => [
  { value: 'all', label: 'All' },
  { value: 'granted', label: 'Granted' },
  ...(isUserMode.value ? [{ value: 'overrides', label: 'Overrides' }] : []),
]);

const description = computed(() => {
  if (!isUserMode.value) {
    return 'Every user with this role receives these permissions.';
  }
  if (!props.roleName) {
    return 'Select a role to see its default permissions.';
  }
  return `Inherited from the ${props.roleName} role. Changes here apply only to ${userLabel.value}.`;
});

const sourceOf = (permission) => {
  if (!isUserMode.value) {
    return props.permissions.includes(permission) ? 'role' : 'off';
  }
  const inRole = props.rolePermissions.includes(permission);
  if (inRole) {
    return props.removePermissions.includes(permission) ? 'removed' : 'role';
  }
  return props.addPermissions.includes(permission) ? 'added' : 'off';
};

const isGranted = (permission) => ['role', 'added'].includes(sourceOf(permission));
const isOverridden = (permission) => ['added', 'removed'].includes(sourceOf(permission));
const countGranted = (permissions) => permissions.filter(isGranted).length;

const grantedCount = computed(() => countGranted(allPermissions));
const addedCount = computed(() => allPermissions.filter(permission => sourceOf(permission) === 'added').length);
const removedCount = computed(() => allPermissions.filter(permission => sourceOf(permission) === 'removed').length);

const sourceLabel = (permission) => ({
  role: `From ${props.roleName} role`,
  added: `Added for ${userLabel.value}`,
  removed: `Removed for ${userLabel.value} (granted by role)`,
  off: 'Not granted',
})[sourceOf(permission)];

const overrideDotClass = (permissions) => {
  if (!isUserMode.value) {
    return '';
  }
  const sources = permissions.map(sourceOf);
  const hasAdded = sources.includes('added');
  const hasRemoved = sources.includes('removed');
  if (hasAdded && hasRemoved) {
    return 'mixed';
  }
  return hasAdded ? 'added' : hasRemoved ? 'removed' : '';
};

const permissionId = (permission) => `permission-${permission.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const matchesFilters = (permission, groupName) => {
  const query = search.value.trim().toLowerCase();
  const haystack = [permission, permissionLabel(permission), groupName].map(text => text.toLowerCase());
  if (query && !haystack.some(text => text.includes(query))) {
    return false;
  }
  if (filter.value === 'granted') {
    return isGranted(permission);
  }
  if (filter.value === 'overrides') {
    return isOverridden(permission);
  }
  return true;
};

const visibleGroups = computed(() => groups
  .filter(group => selectedGroup.value === null || group.name === selectedGroup.value)
  .map(group => ({ name: group.name, permissions: group.permissions.filter(permission => matchesFilters(permission, group.name)) }))
  .filter(group => group.permissions.length));

const toggleIn = (list, permission) => (
  list.includes(permission) ? list.filter(item => item !== permission) : [...list, permission]
);

const toggle = (permission) => {
  if (!isUserMode.value) {
    emit('update:permissions', toggleIn(props.permissions, permission));
  } else if (props.rolePermissions.includes(permission)) {
    emit('update:removePermissions', toggleIn(props.removePermissions, permission));
  } else {
    emit('update:addPermissions', toggleIn(props.addPermissions, permission));
  }
};

const resetOverrides = () => {
  emit('update:addPermissions', []);
  emit('update:removePermissions', []);
};
</script>

<style scoped>
.summary-badge {
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color);
}

.permission-panes {
  display: grid;
  grid-template-columns: 250px 1fr;
  height: 560px;
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
  overflow: hidden;
}

.permission-nav {
  overflow-y: auto;
  padding: 8px;
  background: var(--bs-tertiary-bg);
  border-right: 1px solid var(--bs-border-color);
}

.nav-item-button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 34px;
  padding: 7px 10px;
  border: 0;
  border-radius: 7px;
  background: none;
  color: var(--bs-body-color);
  text-align: left;
}

.nav-item-button:hover {
  background: var(--bs-border-color);
}

.nav-item-button.active {
  background: var(--bs-primary-bg-subtle);
  color: var(--bs-primary);
  font-weight: 600;
}

.group-count {
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.override-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}

.override-dot.added {
  background: var(--bs-success);
}

.override-dot.removed {
  background: var(--bs-danger);
}

.override-dot.mixed {
  background: linear-gradient(90deg, var(--bs-success) 50%, var(--bs-danger) 50%);
}

.permission-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.permission-toolbar {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--bs-border-color);
}

.search-box {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 240px;
  padding-left: 10px;
  border: 1px solid var(--bs-border-color);
  border-radius: 7px;
}

.filter-toggle {
  display: inline-flex;
  border: 1px solid var(--bs-border-color);
  border-radius: 7px;
  overflow: hidden;
}

.filter-toggle button {
  padding: 5px 12px;
  border: 0;
  background: none;
  color: var(--bs-body-color);
  font-size: 13px;
}

.filter-toggle button + button {
  border-left: 1px solid var(--bs-border-color);
}

.filter-toggle button.active {
  background: var(--bs-primary-bg-subtle);
  color: var(--bs-primary);
  font-weight: 600;
}

.permission-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.group-heading {
  padding: 14px 18px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.permission-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 6px 18px;
  border-bottom: 1px solid var(--bs-border-color);
}

.permission-label {
  display: flex;
  flex-direction: column;
  flex: 1;
  cursor: pointer;
}

.permission-name {
  font-weight: 600;
  color: var(--bs-heading-color);
}

.not-granted .permission-name {
  font-weight: 400;
  color: var(--bs-secondary-color);
}

.permission-source {
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.permission-row.added .permission-source {
  color: var(--bs-success);
  font-weight: 600;
}

.permission-row.removed .permission-source {
  color: var(--bs-danger);
  font-weight: 600;
}

.permission-row.removed .permission-name {
  text-decoration: line-through;
}

.undo-button {
  color: var(--bs-secondary-color);
  text-decoration: none;
}

.form-switch .form-check-input {
  width: 2.5em;
  height: 1.35em;
  cursor: pointer;
}

.permission-row.added .form-check-input:checked {
  background-color: var(--bs-success);
  border-color: var(--bs-success);
}

.permission-row.removed .form-check-input {
  border-color: var(--bs-danger);
}
</style>
